import React, { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { clearNotifications, dismissNotification } from '../../redux/notificationsSlice';
import type { NotificationEvent } from '../../types';
import './NotificationsPage.css';

type NotificationFilter = 'all' | 'message' | 'calendar' | 'tizhi' | 'system';

const FILTER_OPTIONS: Array<{ value: NotificationFilter; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'message', label: '互动消息' },
  { value: 'calendar', label: '日程提醒' },
  { value: 'tizhi', label: '体质洞察' },
  { value: 'system', label: '系统通知' },
];

const STATUS_LABEL: Record<string, string> = {
  idle: '未连接',
  connecting: '连接中',
  connected: '实时同步中',
  error: '连接异常',
};

const categorizeEvent = (event: NotificationEvent): NotificationFilter => {
  const type = event.type ?? '';
  if (type.startsWith('calendar')) {
    return 'calendar';
  }
  if (type.startsWith('tizhi')) {
    return 'tizhi';
  }
  if (type === 'message') {
    return 'message';
  }
  return 'system';
};

const describePayload = (event: NotificationEvent): string => {
  const payload = event.payload as Record<string, unknown> | null | undefined;
  if (!payload || typeof payload !== 'object') {
    return '收到一条新的系统通知。';
  }
  const title = payload['title'];
  if (typeof title === 'string' && title.trim()) {
    return title;
  }
  const message = payload['message'];
  if (typeof message === 'string' && message.trim()) {
    return message;
  }
  const content = payload['content'];
  if (typeof content === 'string' && content.trim()) {
    return content;
  }
  const keys = Object.keys(payload);
  if (!keys.length) {
    return '通知详情稍后可查看。';
  }
  const preview = keys
    .slice(0, 3)
    .map((key) => `${key}: ${String(payload[key]).slice(0, 40)}`)
    .join(' · ');
  return preview || '通知详情稍后可查看。';
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '时间未知';
  }
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes <= 1) {
    return '刚刚';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} 分钟前`;
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} 小时前`;
  }
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays} 天前`;
  }
  return date.toLocaleString();
};

const NotificationsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { events, status, lastHeartbeat } = useAppSelector((state) => state.notifications);
  const [filter, setFilter] = useState<NotificationFilter>('all');

  const filteredEvents = useMemo(() => {
    if (filter === 'all') {
      return events;
    }
    return events.filter((event) => categorizeEvent(event) === filter);
  }, [events, filter]);

  const handleDismiss = (id: string) => {
    dispatch(dismissNotification(id));
  };

  const handleClearAll = () => {
    dispatch(clearNotifications());
  };

  return (
    <div className="community-notifications">
      <header>
        <h1>通知中心</h1>
        <p>实时查看社区互动提醒、日程安排与系统公告。</p>
      </header>

      <section className="community-notifications__status">
        <div>
          <span>连接状态：</span>
          <strong>{STATUS_LABEL[status] ?? status}</strong>
        </div>
        <div>
          <span>最新心跳：</span>
          <strong>{lastHeartbeat ? new Date(lastHeartbeat).toLocaleString() : '尚未收到'}</strong>
        </div>
        <button type="button" onClick={handleClearAll} disabled={events.length === 0}>
          清空所有
        </button>
      </section>

      <nav className="community-notifications__filters" aria-label="通知筛选">
        {FILTER_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`community-notifications__filter${filter === option.value ? ' community-notifications__filter--active' : ''}`}
            onClick={() => setFilter(option.value)}
            aria-pressed={filter === option.value}
          >
            {option.label}
          </button>
        ))}
      </nav>

      {filteredEvents.length === 0 ? (
        <div className="community-notifications__placeholder">
          {events.length === 0 ? '暂时没有新通知，持续关注社区动态吧。' : '该筛选条件下暂无通知。'}
        </div>
      ) : (
        <ul className="community-notifications__list">
          {filteredEvents.map((event) => {
            const category = categorizeEvent(event);
            const description = describePayload(event);
            return (
              <li key={event.id} className={`community-notifications__item community-notifications__item--${category}`}>
                <div className="community-notifications__item-header">
                  <span className="community-notifications__item-type">
                    {FILTER_OPTIONS.find((option) => option.value === category)?.label ?? '通知'}
                  </span>
                  <time>{formatTimestamp(event.timestamp)}</time>
                </div>
                <p>{description}</p>
                <button type="button" onClick={() => handleDismiss(event.id)}>
                  标记已读
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default NotificationsPage;
